import React from "react"

// HOCs
import withRecordSignals, {WithRecordSignalsProps} from "../../../hoc/withRecordSignals"

// Models
import {Signal} from "../../../models"

// Components
import ModernEcgGrid from "../ModernEcgGrid"
import ModernCaliperLayer, {EcgMeasurement, ModernCaliperApi} from "../ModernCaliperLayer"

// Geometry
import {computeEcgGeometry, EcgLayoutKey} from "../ModernEcgGrid/geometry"
import {legacyWindowSignals} from "../ModernEcgGrid/window"

// Styles
import useStyles from "./styles"

type ModernEcgStageOwnProps = {
    layout: EcgLayoutKey
    speed: number
    gain: number
    windowStartSec: number
    longRecords: boolean
    caliperOn: boolean
    caliperApiRef: React.MutableRefObject<ModernCaliperApi | null>
    onMeasure(measurement: EcgMeasurement): void
    onCaliperClear(): void
    zoom: number
    boxRef: React.MutableRefObject<HTMLDivElement | null>
}

type ModernEcgStageProps = ModernEcgStageOwnProps & WithRecordSignalsProps

/**
 * @param props
 */
function ModernEcgStage(props: ModernEcgStageProps) {
    const styles = useStyles();
    const {
        layout, speed, gain, windowStartSec, longRecords,
        caliperOn, caliperApiRef, onMeasure, onCaliperClear, zoom, boxRef,
    } = props;
    const [ecgData, setEcgData] = React.useState<Signal[]>([]);

    React.useEffect(() => {
        if (props.signals.length > 0) {
            setEcgData(longRecords ? props.signals : legacyWindowSignals(props.signals));
        }
    }, [props.signals, longRecords]);

    const geometry = React.useMemo(
        () => (ecgData.length === 0
            ? null
            : computeEcgGeometry({ecgData, layout, speed, gain, windowStartSec, longRecords})),
        [ecgData, layout, speed, gain, windowStartSec, longRecords],
    );

    if (geometry === null) {
        return null
    }

    return (
        <div
            className={styles.zoom_box}
            ref={boxRef}
            style={{
                width: Math.round(geometry.widthPx * zoom),
                height: Math.round(geometry.heightPx * zoom),
            }}
        >
            <div className={styles.plot} style={zoom === 1 ? undefined : {transform: `scale(${zoom})`}}>
                <ModernEcgGrid
                    ecgData={ecgData}
                    layout={layout}
                    speed={speed}
                    amplitude={gain}
                    windowStartSec={windowStartSec}
                    longRecords={longRecords}
                />
                {caliperOn && (
                    <ModernCaliperLayer
                        speed={speed}
                        gain={gain}
                        zoom={zoom}
                        xSpanMm={geometry.xSpanMm}
                        ySpanMm={geometry.ySpanMm}
                        apiRef={caliperApiRef}
                        onMeasure={onMeasure}
                        onClear={onCaliperClear}
                    />
                )}
            </div>
        </div>
    )
}

export {ModernEcgStage}

export default withRecordSignals(ModernEcgStage) as React.ComponentType<ModernEcgStageOwnProps>
